import Activity from 'lucide-react/dist/esm/icons/activity.mjs';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle.mjs';
import Bell from 'lucide-react/dist/esm/icons/bell.mjs';
import CheckCircle2 from 'lucide-react/dist/esm/icons/check-circle-2.mjs';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.mjs';
import Droplet from 'lucide-react/dist/esm/icons/droplet.mjs';
import Fuel from 'lucide-react/dist/esm/icons/fuel.mjs';
import Gauge from 'lucide-react/dist/esm/icons/gauge.mjs';
import ListFilter from 'lucide-react/dist/esm/icons/list-filter.mjs';
import Radio from 'lucide-react/dist/esm/icons/radio.mjs';
import SlidersHorizontal from 'lucide-react/dist/esm/icons/sliders-horizontal.mjs';
import Warehouse from 'lucide-react/dist/esm/icons/warehouse.mjs';
import { useEffect, useId, useMemo, useState } from 'react';
import { TANK_METRIC_PRESETS, TankVisualization } from './components/TankVisualization.jsx';
import { tankConfigs } from './data/tanks.js';
import { clamp, compactLiters, liters, percentFull, runtime, statusLabel, tankStatus } from './lib/tankMath.js';

const cloneTanks = () =>
  tankConfigs.map((tank) => ({
    ...tank,
    pumps: tank.pumps.map((pump) => ({ ...pump, history: [...pump.history] })),
  }));

function tankVisualizationProps(tank) {
  return {
    capacityLiters: tank.capacityLiters,
    id: tank.id,
    minDispensableLiters: tank.minDispensableLiters,
    product: tank.product,
    redZoneLiters: tank.redZoneLiters,
    status: tankStatus(tank),
    type: tank.type,
    volumeLiters: tank.volumeLiters,
    warningLiters: tank.warningLiters,
  };
}

function advanceTelemetry(tanks) {
  return tanks.map((tank) => {
    let activeOutflowLpm = 0;

    const pumps = tank.pumps.map((pump) => {
      const isActive = pump.status === 'running' || pump.status === 'warning';

      if (!isActive) {
        return {
          ...pump,
          flowRateLpm: 0,
          pressureBar: pump.status === 'stopped' ? 0 : pump.pressureBar,
          history: [...pump.history.slice(-27), 0],
        };
      }

      const low = pump.nominalFlowLpm * (pump.status === 'warning' ? 0.46 : 0.82);
      const high = pump.nominalFlowLpm * (pump.status === 'warning' ? 0.92 : 1.13);
      const nextFlow = clamp(pump.flowRateLpm + (Math.random() - 0.46) * pump.nominalFlowLpm * 0.09, low, high);
      const nextPressure = clamp(pump.pressureBar + (Math.random() - 0.5) * (pump.status === 'warning' ? 0.18 : 0.08), 0.7, 8.5);

      activeOutflowLpm += nextFlow;

      return {
        ...pump,
        flowRateLpm: nextFlow,
        pressureBar: nextPressure,
        runtimeSeconds: pump.runtimeSeconds + 1,
        totalDispensedLiters: pump.totalDispensedLiters + nextFlow / 60,
        history: [...pump.history.slice(-27), Math.round(nextFlow)],
      };
    });

    const inletLpm = (tank.inletLpm ?? 0) * (0.66 + Math.random() * 0.22);
    const nextVolume = clamp(tank.volumeLiters + inletLpm / 60 - activeOutflowLpm / 60, 0, tank.capacityLiters);

    return {
      ...tank,
      volumeLiters: nextVolume,
      pumps,
      lastUpdated: new Date().toISOString(),
    };
  });
}

function App() {
  const [tanks, setTanks] = useState(cloneTanks);
  const [selectedId, setSelectedId] = useState('T-101');

  useEffect(() => {
    const timer = window.setInterval(() => {
      setTanks((current) => advanceTelemetry(current));
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  const selectedTank = tanks.find((tank) => tank.id === selectedId) ?? tanks[0];
  const stats = useMemo(() => computeStats(tanks), [tanks]);
  const alarms = useMemo(() => buildAlarms(tanks), [tanks]);

  return (
    <div className="app-shell">
      <main className="workspace">
        <section className="metric-grid" aria-label="Depot stock summary">
          <MetricCard
            icon={<Warehouse />}
            label="Total stock"
            value={`${liters(stats.totalStock)} L`}
            sub={`${Math.round(stats.utilization * 100)}% of ${compactLiters(stats.capacity)}`}
            tone="green"
          />
          <MetricCard
            icon={<Droplet />}
            label="Usable stock"
            value={`${liters(stats.usableStock)} L`}
            sub={`${Math.round(stats.usableRatio * 100)}% available for dispatch`}
            tone="teal"
          />
          <MetricCard
            icon={<AlertTriangle />}
            label="Critical tanks"
            value={stats.criticalCount}
            sub={stats.criticalCount === 1 ? '1 tank below safe level' : 'No critical tank levels'}
            tone="red"
          />
          <MetricCard
            icon={<AlertTriangle />}
            label="Warning tanks"
            value={stats.warningCount}
            sub={`${stats.warningCount} tanks require attention`}
            tone="amber"
          />
          <MetricCard
            icon={<Fuel />}
            label="Active transfers"
            value={stats.activePumps}
            sub={`Total flow ${liters(stats.totalFlow)} L/min`}
            tone="green"
          />
          <MetricCard
            icon={<Gauge />}
            label="Utilization"
            value={`${Math.round(stats.utilization * 100)}%`}
            sub={`${liters(stats.totalStock)} L in stock`}
            tone="ring"
          />
        </section>

        <section className="dashboard-grid">
          <TankOverview tanks={tanks} selectedId={selectedId} onSelect={setSelectedId} />
          <SelectedTankPanel tank={selectedTank} />
          <PumpPanel tank={selectedTank} />
        </section>

        <AlarmStrip alarms={alarms} />
      </main>
    </div>
  );
}

function computeStats(tanks) {
  const totalStock = tanks.reduce((sum, tank) => sum + tank.volumeLiters, 0);
  const capacity = tanks.reduce((sum, tank) => sum + tank.capacityLiters, 0);
  const usableStock = tanks.reduce((sum, tank) => sum + Math.max(0, tank.volumeLiters - tank.minDispensableLiters), 0);
  const usableCapacity = tanks.reduce((sum, tank) => sum + Math.max(0, tank.capacityLiters - tank.minDispensableLiters), 0);
  const statuses = tanks.map(tankStatus);
  const activePumps = tanks
    .flatMap((tank) => tank.pumps)
    .filter((pump) => pump.status === 'running' || pump.status === 'warning');

  return {
    totalStock,
    capacity,
    usableStock,
    usableRatio: capacity > 0 ? usableStock / usableCapacity : 0,
    utilization: capacity > 0 ? totalStock / capacity : 0,
    criticalCount: statuses.filter((status) => status === 'critical').length,
    warningCount: statuses.filter((status) => status === 'warning').length,
    activePumps: activePumps.length,
    totalFlow: activePumps.reduce((sum, pump) => sum + pump.flowRateLpm, 0),
  };
}

function buildAlarms(tanks) {
  const tankAlarms = tanks
    .map((tank) => {
      const status = tankStatus(tank);
      if (status === 'normal') return null;

      return {
        id: tank.id,
        tone: status,
        title: `${tank.id} ${tank.product}`,
        body:
          status === 'critical'
            ? `Level below red zone (${Math.round(percentFull(tank) * 100)}%)`
            : `Level in warning band (${Math.round(percentFull(tank) * 100)}%)`,
        time: tank.lastUpdated,
      };
    })
    .filter(Boolean);

  const pumpAlarms = tanks.flatMap((tank) =>
    tank.pumps
      .filter((pump) => pump.status === 'warning')
      .map((pump) => ({
        id: pump.id,
        tone: 'warning',
        title: `${pump.id} ${tank.id}`,
        body: 'Flow below nominal range',
        time: tank.lastUpdated,
      })),
  );

  return [...tankAlarms, ...pumpAlarms].slice(0, 4);
}

function MetricCard({ icon, label, value, sub, tone }) {
  return (
    <article className={`metric-card tone-${tone}`}>
      <div className="metric-icon">{icon}</div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{sub}</small>
      </div>
    </article>
  );
}

function TankOverview({ tanks, selectedId, onSelect }) {
  return (
    <section className="surface overview-panel">
      <PanelHeader
        title="Tank overview"
        actions={[
          <button className="icon-button small" type="button" aria-label="Filter tanks" key="filter">
            <ListFilter size={16} />
          </button>,
          <button className="icon-button small" type="button" aria-label="Configure view" key="settings">
            <SlidersHorizontal size={16} />
          </button>,
        ]}
      />

      <div className="tank-list">
        {tanks.map((tank) => (
          <TankCard key={tank.id} tank={tank} selected={tank.id === selectedId} onClick={() => onSelect(tank.id)} />
        ))}
      </div>
    </section>
  );
}

function TankCard({ tank, selected, onClick }) {
  const status = tankStatus(tank);
  const percent = Math.round(percentFull(tank) * 100);

  return (
    <button className={`tank-card ${selected ? 'selected' : ''} status-${status}`} type="button" onClick={onClick}>
      <div className="tank-card-top">
        <span>
          <i className={`status-dot ${status}`} />
          <strong>{tank.id}</strong>
        </span>
        <em>{tank.product}</em>
      </div>
      <TankVisualization {...tankVisualizationProps(tank)} variant="mini" metrics={TANK_METRIC_PRESETS.preview} />
      <div className="tank-card-bottom">
        <strong>{percent}%</strong>
        <span>{compactLiters(tank.volumeLiters)}</span>
      </div>
    </button>
  );
}

function SelectedTankPanel({ tank }) {
  const status = tankStatus(tank);
  const percent = Math.round(percentFull(tank) * 100);
  const activeFlow = tank.pumps
    .filter((pump) => pump.status === 'running' || pump.status === 'warning')
    .reduce((sum, pump) => sum + pump.flowRateLpm, 0);

  return (
    <section className={`surface selected-panel status-${status}`}>
      <div className="selected-header">
        <div>
          <h2>{tank.id}</h2>
          <span className="product-line">
            <i className={`status-dot ${status}`} />
            {tank.product}
            <StatusPill status={status} />
          </span>
        </div>
        <div className="update-stamp">
          <Radio size={18} />
          Last update: {formatTime(tank.lastUpdated)}
        </div>
      </div>

      <div className={`selected-body ${tank.type === 'horizontal-cylinder' ? 'horizontal-layout' : ''}`}>
        <div className="tank-visual-wrap">
          <TankVisualization {...tankVisualizationProps(tank)} variant="large" metrics={TANK_METRIC_PRESETS.detail} />
          <div className="connection-rail">
            <span>To pumps</span>
            <i />
            <span>To header</span>
          </div>
        </div>

        <aside className="detail-stack">
          <DetailRow
            label="Volume"
            value={`${liters(tank.volumeLiters)} L`}
            sub={`${percent}% of ${liters(tank.capacityLiters)} L`}
          />
          <DetailRow label="Product" value={tank.product} icon={<Fuel size={22} />} />
          <DetailRow label="Tank capacity" value={`${liters(tank.capacityLiters)} L`} />
          <DetailRow
            label="Min. dispensable"
            value={`${liters(tank.minDispensableLiters)} L (${Math.round((tank.minDispensableLiters / tank.capacityLiters) * 100)}%)`}
            marker="min"
          />
          <DetailRow
            label="Red zone"
            value={`${liters(tank.redZoneLiters)} L (${Math.round((tank.redZoneLiters / tank.capacityLiters) * 100)}%)`}
            marker="red"
          />
          <DetailRow
            label="Current flow"
            value={`${liters(activeFlow)} L/min`}
            sub={`${tank.pumps.length} connected pumps`}
            icon={<Activity size={22} />}
          />
          <StatusSummary status={status} />
        </aside>
      </div>
    </section>
  );
}

function DetailRow({ label, value, sub, icon, marker }) {
  return (
    <div className="detail-row">
      <span>{label}</span>
      <strong>{value}</strong>
      {sub ? <small>{sub}</small> : null}
      {icon ? <em>{icon}</em> : null}
      {marker ? <i className={`limit-marker ${marker}`} /> : null}
    </div>
  );
}

function StatusSummary({ status }) {
  return (
    <div className="status-summary">
      {status === 'normal' ? <CheckCircle2 size={22} /> : <AlertTriangle size={22} />}
      <div>
        <span>Tank status</span>
        <strong>{statusLabel(status)}</strong>
        <small>{status === 'normal' ? 'No active alarms' : 'Operator review required'}</small>
      </div>
    </div>
  );
}

function PumpPanel({ tank }) {
  return (
    <section className="surface pump-panel">
      <PanelHeader
        title={`Pumps (${tank.pumps.length})`}
        actions={[
          <button className="text-link" type="button" key="all">
            View all pumps
          </button>,
        ]}
      />
      <div className="pump-list">
        {tank.pumps.map((pump) => (
          <PumpCard pump={pump} key={pump.id} />
        ))}
      </div>
    </section>
  );
}

function PumpCard({ pump }) {
  const status = pump.status;
  const isActive = status === 'running' || status === 'warning';

  return (
    <article className={`pump-card status-${status}`}>
      <div className="pump-card-header">
        <div>
          <h3>
            <i className={`status-dot ${status === 'stopped' ? 'offline' : status}`} />
            {pump.id}
          </h3>
          <span>{pump.role}</span>
        </div>
        <StatusPill status={status} />
      </div>

      <div className="pump-content">
        <div className="pump-metrics">
          <MetricLine label="Flow rate" value={`${liters(pump.flowRateLpm)} L/min`} strong={isActive} />
          <MetricLine label="Discharge pressure" value={`${pump.pressureBar.toFixed(1)} bar`} />
          <MetricLine label="Run time" value={runtime(pump.runtimeSeconds)} />
        </div>
        <PumpVector active={isActive} status={status} />
      </div>

      <div className="dispensed-row">
        <span>Total dispensed</span>
        <strong>{liters(pump.totalDispensedLiters)} L</strong>
        <Sparkline data={pump.history} active={isActive} />
      </div>
    </article>
  );
}

function MetricLine({ label, value, strong }) {
  return (
    <div className="metric-line">
      <span>{label}</span>
      <strong className={strong ? 'active' : ''}>{value}</strong>
    </div>
  );
}

function AlarmStrip({ alarms }) {
  return (
    <section className="alarm-strip" aria-label="Active alarms">
      <button className="alarm-toggle" type="button">
        <Bell size={18} />
        Active alarms ({alarms.length})
        <ChevronDown size={15} />
      </button>
      <div className="alarm-items">
        {alarms.map((alarm) => (
          <article className={`alarm-item tone-${alarm.tone}`} key={`${alarm.id}-${alarm.body}`}>
            <AlertTriangle size={18} />
            <div>
              <strong>{alarm.title}</strong>
              <span>{alarm.body}</span>
            </div>
            <time>{formatTime(alarm.time)}</time>
          </article>
        ))}
      </div>
      <button className="text-link alarm-link" type="button">
        View all alarms
      </button>
    </section>
  );
}

function PanelHeader({ title, actions = [] }) {
  return (
    <div className="panel-header">
      <h2>{title}</h2>
      <div>{actions}</div>
    </div>
  );
}

function StatusPill({ status }) {
  return <span className={`status-pill ${status}`}>{statusLabel(status)}</span>;
}

function PumpVector({ active, status }) {
  const reactId = useId().replaceAll(':', '');
  const pumpId = `pump-${reactId}`;
  const motorId = `motor-${reactId}`;

  return (
    <svg className={`pump-vector status-${status} ${active ? 'active' : ''}`} viewBox="0 0 270 118" aria-hidden="true">
      <defs>
        <linearGradient id={pumpId} x1="0%" x2="100%" y1="0%" y2="0%">
          <stop offset="0%" stopColor="color-mix(in srgb, var(--pump-color) 30%, #ecf4f4)" />
          <stop offset="44%" stopColor="color-mix(in srgb, var(--pump-color) 62%, #d4e4e2)" />
          <stop offset="100%" stopColor="color-mix(in srgb, var(--pump-color) 38%, #b8c9cb)" />
        </linearGradient>
        <linearGradient id={motorId} x1="0%" x2="100%" y1="0%" y2="0%">
          <stop offset="0%" stopColor="#d9e5e7" />
          <stop offset="52%" stopColor="color-mix(in srgb, var(--pump-color) 46%, #d9e5e7)" />
          <stop offset="100%" stopColor="#93a9ad" />
        </linearGradient>
      </defs>
      <path className="pump-skid-shadow" d="M22 101h228l16 8H9z" />
      <path className="pump-base" d="M18 88h231v12H18zM36 77h180v11H36z" />
      <path className="pump-anchor-bolts" d="M37 94h0M69 94h0M104 94h0M181 94h0M218 94h0M241 94h0" />
      <path className="pump-pipe" d="M9 61h35M76 61h26M165 61h25M239 61h23" />
      <path className="pump-flow" d="M17 61h23M169 61h16M242 61h14" />
      <path className="pump-flange" d="M43 49h8v24h-8zM72 49h8v24h-8zM101 48h10v27h-10zM188 48h10v27h-10z" />
      <circle className="pump-valve" cx="61" cy="61" r="19" fill={`url(#${pumpId})`} />
      <path className="pump-valve-handle" d="M61 42v-9M50 33h22" />
      <path className="pump-body" d="M111 42h45c12 0 22 10 22 22s-10 22-22 22h-45z" fill={`url(#${pumpId})`} />
      <path className="pump-volute" d="M127 40c18-13 43 1 43 24 0 21-22 35-41 25 8-5 12-14 12-25 0-10-5-19-14-24z" />
      <rect className="pump-neck" x="130" y="22" width="30" height="20" rx="3" fill={`url(#${pumpId})`} />
      <path className="pump-gauge" d="M145 22v-10M136 12h18" />
      <path className="pump-coupling" d="M177 54h20v20h-20zM184 48v32" />
      <rect className="pump-motor" x="197" y="42" width="49" height="45" rx="7" fill={`url(#${motorId})`} />
      <path className="pump-fins" d="M205 49h42M205 57h42M205 65h42M205 73h42M205 81h42" />
      <circle className="pump-rotor" cx="149" cy="64" r="15" />
      <path className="pump-rotor-mark" d="M149 51v13l9 8" />
      <path className="pump-highlight" d="M119 47h26M204 47h33" />
    </svg>
  );
}

function Sparkline({ data, active }) {
  const width = 148;
  const height = 38;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = Math.max(1, max - min);
  const points = data
    .map((value, index) => {
      const x = (index / Math.max(1, data.length - 1)) * width;
      const y = height - ((value - min) / range) * (height - 8) - 4;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg className={`sparkline ${active ? 'active' : ''}`} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <polyline points={points} />
    </svg>
  );
}

function formatTime(value) {
  const date = value ? new Date(value) : new Date();
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
}

export default App;
